/* EmailTemplates — /admin/email-templates */
import { useState, useRef } from 'react';
import css from '../AdminPage.module.css';
import {
  useEmailTemplates,
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  useDeleteEmailTemplate,
  useEmailTokens,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import type { EmailTemplate } from '../../../types/admin';
import RichEmailEditor from '../../../components/common/RichEmailEditor';

export default function EmailTemplates() {
  const { data: templates = [], isLoading } = useEmailTemplates();
  const createTemplate = useCreateEmailTemplate();
  const updateTemplate = useUpdateEmailTemplate();
  const deleteTemplate = useDeleteEmailTemplate();
  const { data: tokenGroups = {} } = useEmailTokens();
  const { showToast } = useToast();

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<EmailTemplate | null>(null);
  const [search, setSearch] = useState('');

  /* form */
  const [eName, setEName] = useState('');
  const [eCategory, setECategory] = useState('');
  const [eRecipient, setERecipient] = useState('');
  const [eSubject, setESubject] = useState('');
  const [eBody, setEBody] = useState('');
  const [tokenGroup, setTokenGroup] = useState('');
  const [insertTarget, setInsertTarget] = useState<'body' | 'subject'>('body');
  const subjectRef = useRef<HTMLInputElement>(null);

  const filtered = search
    ? templates.filter(
        (t) =>
          t.name.toLowerCase().includes(search.toLowerCase()) ||
          t.subject.toLowerCase().includes(search.toLowerCase()),
      )
    : templates;

  function openNew() {
    setEditing(null);
    setEName('');
    setECategory('');
    setERecipient('');
    setESubject('');
    setEBody('');
    setTokenGroup('');
    setInsertTarget('body');
    setShowModal(true);
  }
  function openEdit(t: EmailTemplate) {
    setEditing(t);
    setEName(t.name);
    setECategory(t.category || '');
    setERecipient(t.recipient_type || '');
    setESubject(t.subject);
    setEBody(t.body || '');
    setTokenGroup('');
    setInsertTarget('body');
    setShowModal(true);
  }

  function insertToken(token: string) {
    const tag = `{{${token}}}`;
    if (insertTarget === 'subject') {
      const el = subjectRef.current;
      if (el) {
        const start = el.selectionStart ?? eSubject.length;
        const end = el.selectionEnd ?? eSubject.length;
        const before = eSubject.slice(0, start);
        const after = eSubject.slice(end);
        setESubject(before + tag + after);
        setTimeout(() => {
          el.focus();
          el.selectionStart = el.selectionEnd = start + tag.length;
        }, 0);
      } else {
        setESubject(eSubject + tag);
      }
    } else {
      // Insert token at end of body for RichEmailEditor
      setEBody(eBody + ' ' + tag);
    }
  }

  async function save() {
    const payload = {
      name: eName,
      category: eCategory,
      recipient_type: eRecipient,
      subject: eSubject,
      body: eBody,
    };
    try {
      if (editing) {
        await updateTemplate.mutateAsync({ id: editing.id, ...payload });
        showToast('Template updated', 'success');
      } else {
        await createTemplate.mutateAsync(payload);
        showToast('Template created', 'success');
      }
      setShowModal(false);
    } catch {
      showToast('Failed to save template', 'error');
    }
  }

  async function remove(id: number) {
    if (!confirm('Delete this email template?')) return;
    try {
      await deleteTemplate.mutateAsync(id);
      showToast('Template deleted', 'success');
    } catch {
      showToast('Failed to delete', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  const groupNames = Object.keys(tokenGroups);

  return (
    <>
      <div className={css.section}>
        <div className={css.sectionHeader}>
          <div>
            <h2 className={css.sectionTitle}>✉️ Email Templates</h2>
            <p className={css.sectionSubtitle}>
              {templates.length} template{templates.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className={css.toolbar}>
            <div className={css.searchWrapper}>
              <span className={css.searchIcon}>🔍</span>
              <input
                className={css.searchInput}
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button className={css.btnPrimary} onClick={openNew}>
              + New Template
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className={css.emptyState}>
            <div className={css.emptyIcon}>✉️</div>
            <p className={css.emptyMessage}>No email templates found</p>
          </div>
        ) : (
          <table className={css.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Subject</th>
                <th>Recipient</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td>{t.category || '—'}</td>
                  <td
                    style={{
                      maxWidth: 250,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.subject}
                  </td>
                  <td>{t.recipient_type || '—'}</td>
                  <td style={{ fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>
                    {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className={`${css.btnSecondary} ${css.btnSmall}`}
                        onClick={() => openEdit(t)}
                      >
                        Edit
                      </button>
                      <button
                        className={`${css.btnDanger} ${css.btnSmall}`}
                        onClick={() => remove(t.id)}
                      >
                        Del
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className={css.modalBackdrop} onClick={() => setShowModal(false)}>
          <div className={css.modalLg} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>
                {editing ? 'Edit Email Template' : 'New Email Template'}
              </h3>
              <button className={css.modalClose} onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.grid2}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Template Name</label>
                  <input
                    className={css.formInput}
                    value={eName}
                    onChange={(e) => setEName(e.target.value)}
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Category</label>
                  <input
                    className={css.formInput}
                    value={eCategory}
                    onChange={(e) => setECategory(e.target.value)}
                    placeholder="e.g. Notifications"
                  />
                </div>
              </div>
              <div className={css.grid2}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Recipient Type</label>
                  <select
                    className={css.formSelect}
                    value={eRecipient}
                    onChange={(e) => setERecipient(e.target.value)}
                  >
                    <option value="">Any</option>
                    <option value="client">Client</option>
                    <option value="defendant">Defendant</option>
                    <option value="court">Court</option>
                    <option value="internal">Internal</option>
                  </select>
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Subject</label>
                  <input
                    ref={subjectRef}
                    className={css.formInput}
                    value={eSubject}
                    onChange={(e) => setESubject(e.target.value)}
                    onFocus={() => setInsertTarget('subject')}
                    placeholder="Use {{merge_field}} for placeholders"
                  />
                </div>
              </div>

              {/* Token picker */}
              <div className={css.formGroup}>
                <label className={css.formLabel}>Insert Token</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className={css.formSelect}
                    style={{ flex: '0 0 120px' }}
                    value={insertTarget}
                    onChange={(e) => setInsertTarget(e.target.value as 'body' | 'subject')}
                  >
                    <option value="body">→ Body</option>
                    <option value="subject">→ Subject</option>
                  </select>
                  <select
                    className={css.formSelect}
                    style={{ flex: '0 0 160px' }}
                    value={tokenGroup}
                    onChange={(e) => setTokenGroup(e.target.value)}
                  >
                    <option value="">Select category…</option>
                    {groupNames.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                  {tokenGroup && tokenGroups[tokenGroup] && (
                    <div className={css.tagList} style={{ flex: 1 }}>
                      {tokenGroups[tokenGroup].map((t) => (
                        <button
                          key={t.token}
                          type="button"
                          className={css.tag}
                          style={{ cursor: 'pointer' }}
                          title={t.description}
                          onClick={() => insertToken(t.token)}
                        >
                          {`{{${t.token}}}`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Body with Rich Text Editor */}
              <div className={css.formGroup}>
                <label className={css.formLabel}>Body (HTML supported)</label>
                <RichEmailEditor
                  value={eBody}
                  onChange={setEBody}
                  placeholder="Dear {{client_name}},..."
                  mergeFields={[]}
                  height={300}
                />
              </div>

              {editing?.merge_fields && editing.merge_fields.length > 0 && (
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Detected Merge Fields</label>
                  <div className={css.tagList}>
                    {editing.merge_fields.map((f) => (
                      <span key={f} className={css.tag}>{`{{${f}}}`}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className={css.btnPrimary} onClick={save} disabled={!eName || !eSubject}>
                {editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
